/**
 * KV / localStorage Startup-Health (ADR-0002).
 */

import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { listKvKeys, kvGetRaw, kvRemove } from '../infrastructure/storage/kvAccess';
import {
  getCurrentBusinessId,
  isEntityShellBlob,
  readBestBusinessMergedPayload,
  scoreBusinessMergedPayload
} from './sharedCore';
import { removeLegacyStandaloneLotsKey } from './lotsReadSource';
import { cleanupOldKeys, needsCleanup } from './cleanupOldKeys';
import {
  REGISTRATION_PROTECTED_KV_KEYS,
  isRegistrationProtectedKey,
  isCleanupProtectedKey
} from './storageProtectedKeys';

export { REGISTRATION_PROTECTED_KV_KEYS, isRegistrationProtectedKey, isCleanupProtectedKey };

export const LEGACY_STANDALONE_DOMAIN_KEYS = [
  'lots', 'customers', 'sales', 'products', 'suppliers',
  'purchases', 'orders', 'invoices', 'expenses'
];

/** Verwaiste Keys ohne Business-Payload (nicht mehr schreiben). */
export const ORPHAN_STANDALONE_KEYS = ['payments'];

const CORRUPTED_RAW = new Set(['[object Object]', '"[object Object]"']);

function isCorruptedRaw(raw) {
  if (raw == null) return false;
  const s = String(raw).trim();
  return CORRUPTED_RAW.has(s) || s === '';
}

export function purgeCorruptedKvEntries() {
  const removed = [];
  for (const key of listKvKeys()) {
    if (!key || isCleanupProtectedKey(key)) continue;
    try {
      if (isCorruptedRaw(kvGetRaw(key))) {
        kvRemove(key);
        removed.push(key);
      }
    } catch {
      try { kvRemove(key); removed.push(key); } catch { /* ignore */ }
    }
  }
  return { removed };
}

function hasCanonicalBusinessSource(businessId) {
  if (!businessId) return false;
  if (isEntityStoreNoKvBlobEnabled()) return true;
  const { parsed } = readBestBusinessMergedPayload(businessId);
  if (!parsed || typeof parsed !== 'object') return false;
  if (isEntityShellBlob(parsed)) return true;
  return scoreBusinessMergedPayload(parsed) > 0;
}

export function purgeLegacyStandaloneDomainKeys(businessId) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: true });
  const removed = [];
  if (!hasCanonicalBusinessSource(bid)) {
    return { removed, skipped: true, reason: 'no_canonical_source' };
  }
  for (const key of LEGACY_STANDALONE_DOMAIN_KEYS) {
    if (key === 'lots') continue;
    if (isRegistrationProtectedKey(key)) continue;
    try {
      if (kvGetRaw(key) != null) { kvRemove(key); removed.push(key); }
    } catch { /* ignore */ }
  }
  for (const key of ORPHAN_STANDALONE_KEYS) {
    try {
      if (kvGetRaw(key) != null) { kvRemove(key); removed.push(key); }
    } catch { /* ignore */ }
  }
  if (bid) {
    try { if (removeLegacyStandaloneLotsKey(bid)) removed.push('lots'); } catch { /* ignore */ }
  }
  return { removed, skipped: false };
}

const LOG_RETENTION_DAYS = 30;

/** Entfernt alte `logs_YYYY-MM-DD` Diagnose-Keys. */
export function purgeStaleLogKeys() {
  const removed = [];
  const cutoff = Date.now() - LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000;

  for (const key of listKvKeys()) {
    if (!key?.startsWith('logs_')) continue;
    const dateStr = key.slice(5);
    const parsed = new Date(dateStr);
    if (!Number.isNaN(parsed.getTime()) && parsed.getTime() < cutoff) {
      try {
        kvRemove(key);
        removed.push(key);
      } catch {
        /* ignore */
      }
    }
  }

  return { removed };
}

/** Nach Migration/Recovery — Legacy-Keys wenn kanonische Quelle jetzt existiert. */
export async function runLocalStoragePostRecoveryCleanup() {
  let keyConsolidation = null;
  try {
    const { runKvKeyConsolidation } = await import('../infrastructure/storage/kvKeyConsolidation');
    const { getCurrentBusinessId } = await import('./sharedCore');
    keyConsolidation = await runKvKeyConsolidation({
      mode: 'startup',
      businessId: getCurrentBusinessId({ allowFallback: true })
    });
  } catch (e) {
    keyConsolidation = { ok: false, error: e?.message || String(e) };
  }

  const legacy = purgeLegacyStandaloneDomainKeys();
  const pattern = purgeLegacyPatternKeys();
  const logs = purgeStaleLogKeys();
  const result = {
    ok: true,
    keyConsolidation,
    legacyRemoved: legacy.removed,
    legacySkipped: legacy.skipped === true,
    patternCleanedKeys: pattern.cleanedKeys || 0,
    logsRemoved: logs.removed
  };
  const total =
    legacy.removed.length + (pattern.cleanedKeys || 0) + logs.removed.length;
  if (total > 0) {
    console.info('[Storage Health] Post-recovery cleanup:', result);
  }
  return result;
}

/** Alte Muster: stock_data_*, lots_*, persistent_stock_data, … */
export function purgeLegacyPatternKeys() {
  if (!needsCleanup()) {
    return { cleanedKeys: 0, skipped: true };
  }
  const result = cleanupOldKeys();
  return {
    cleanedKeys: result.cleanedKeys || 0,
    skipped: false,
    success: result.success !== false
  };
}

/**
 * Vollständige System-Bereinigung (Startup + optional Deep-Clean).
 * @param {{ deep?: boolean }} [options] — deep: performAdvancedCleanup (Backups, Duplikate)
 */
export async function runStorageSystemCleanup(options = {}) {
  const corrupted = purgeCorruptedKvEntries();
  const businessId = getCurrentBusinessId({ allowFallback: true });
  const legacy = purgeLegacyStandaloneDomainKeys(businessId);
  const pattern = purgeLegacyPatternKeys();
  const logs = purgeStaleLogKeys();

  let deep = null;
  if (options.deep) {
    try {
      const { performAdvancedCleanup } = await import('./advancedLocalStorageCleanup');
      deep = performAdvancedCleanup();
    } catch (e) {
      deep = { success: false, error: e?.message || String(e) };
    }
  }

  const totalRemoved =
    corrupted.removed.length +
    legacy.removed.length +
    (pattern.cleanedKeys || 0) +
    logs.removed.length +
    (deep?.removedKeys?.length || 0);

  const result = {
    ok: true,
    businessId: businessId || null,
    corruptedRemoved: corrupted.removed,
    legacyRemoved: legacy.removed,
    legacySkipped: legacy.skipped === true,
    patternCleanup: pattern,
    logsRemoved: logs.removed,
    deepCleanup: deep,
    totalRemoved
  };

  if (totalRemoved > 0 || options.deep) {
    console.info('[Storage Health] System cleanup:', result);
  }

  return result;
}

export async function runLocalStorageStartupHealth() {
  const corrupted = purgeCorruptedKvEntries();
  const businessId = getCurrentBusinessId({ allowFallback: true });
  const legacy = purgeLegacyStandaloneDomainKeys(businessId);
  const pattern = purgeLegacyPatternKeys();
  const logs = purgeStaleLogKeys();
  const result = {
    ok: true,
    businessId: businessId || null,
    corruptedRemoved: corrupted.removed,
    legacyRemoved: legacy.removed,
    legacySkipped: legacy.skipped === true,
    patternCleanedKeys: pattern.cleanedKeys || 0,
    patternSkipped: pattern.skipped === true,
    logsRemoved: logs.removed
  };
  const total =
    corrupted.removed.length +
    legacy.removed.length +
    (pattern.cleanedKeys || 0) +
    logs.removed.length;
  if (total > 0) {
    console.info('[Storage Health] KV startup cleanup:', result);
  }
  return result;
}

export async function runLocalStorageFullHealthReport(options = {}) {
  const startup = await runLocalStorageStartupHealth();
  let audit = null;
  const wantAudit = options.includeAudit === true || process.env.NODE_ENV === 'development';
  if (wantAudit && options.includeAudit !== false) {
    try {
      const { localStorageAudit } = await import('./localStorageAudit');
      audit = await localStorageAudit.runFullAudit();
    } catch (e) {
      audit = { error: e?.message || String(e) };
    }
  }
  let entityOnly = null;
  let businessIdForEntity = null;
  if (options.includeEntityOnly !== false && isEntityStoreNoKvBlobEnabled()) {
    try {
      const { verifyEntityOnlyStorageHealth } = await import(
        '../infrastructure/storage/entityOnlyStorageHealth'
      );
      businessIdForEntity =
        startup.businessId || getCurrentBusinessId({ allowFallback: true });
      if (businessIdForEntity) {
        entityOnly = await verifyEntityOnlyStorageHealth(businessIdForEntity);
      }
    } catch (e) {
      entityOnly = { error: e?.message || String(e) };
    }
  }
  const auditIssues = audit && !audit.error ? countCriticalAuditIssues(audit) : 0;

  // Opt out with allowSelfHeal:false (e.g. Storage-Gesundheit) — default keeps startup self-heal
  if (
    options.allowSelfHeal !== false &&
    entityOnly &&
    !entityOnly.skipped &&
    !entityOnly.ok &&
    businessIdForEntity
  ) {
    try {
      const { repairEntityOnlyStorageMeta } = await import(
        '../infrastructure/storage/entityOnlyStorageHealth'
      );
      const repaired = await repairEntityOnlyStorageMeta(businessIdForEntity);
      if (repaired.repaired) {
        entityOnly = repaired;
      }
    } catch {
      /* ignore */
    }
  }

  const entityBlocking =
    entityOnly &&
    !entityOnly.skipped &&
    !entityOnly.ok &&
    !entityOnly.error &&
    (entityOnly.issues || []).some((i) =>
      ['payload_meta_not_migrated', 'payload_version_zero'].includes(i)
    );

  return {
    ok: startup.ok && auditIssues === 0 && !entityBlocking,
    startup,
    audit,
    entityOnly,
    auditCriticalIssues: auditIssues
  };
}

function countCriticalAuditIssues(audit) {
  let count = (audit.duplicates?.length || 0) + (audit.inconsistencies?.length || 0);
  for (const issue of audit.issues || []) {
    if (issue.type === 'parse_error' || issue.type === 'circular_reference') {
      count += 1;
    }
  }
  return count;
}

/** @deprecated use countCriticalAuditIssues — exported for DevTools */
export function summarizeAuditSeverity(audit) {
  if (!audit || audit.error) return { critical: 0, informational: 0 };
  const informational = (audit.issues || []).filter((i) =>
    ['temporary_keys', 'large_keys'].includes(i.type)
  ).length;
  return {
    critical: countCriticalAuditIssues(audit),
    informational
  };
}

export function isLegacyStandaloneDomainKey(key) {
  return LEGACY_STANDALONE_DOMAIN_KEYS.includes(String(key));
}

export default runLocalStorageStartupHealth;
